import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  HStack,
  Select,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Stack,
  Divider,
  IconButton,
  // ⭐️ 모달 관련
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react'
import { SearchIcon, CopyIcon } from '@chakra-ui/icons'
import { approveTRC, getDepositList } from '../../services/apiService'
import dayjs from 'dayjs'
// ⭐️ 승인 API 임포트
// ⭐️ adminId 사용 (다른 페이지에서 쓰던 패턴 그대로)
import { useRecoilState } from 'recoil'
import { authState } from '../../atoms/auth'

type DepositItem = {
  id: number
  userId: number
  usdtAmount: string | null
  txid: string | null
  network: string | null
  walletAddress: string | null
  createdAt: string | null
  beforeUsdtBalance: string | null
  afterUsdtBalance: string | null
  account?: string
  username?: string
  phone?: string
}

const Deposit = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure() // ⭐️ 모달 제어
  const [auth] = useRecoilState(authState) // ⭐️ adminId 획득

  // 검색/페이지네이션 상태
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<DepositItem[]>([])
  const [loading, setLoading] = useState(false)

  // ⭐️ 승인 모달 상태
  const [selected, setSelected] = useState<DepositItem | null>(null)
  const [approveAmount, setApproveAmount] = useState<string>('')
  const [approveLoading, setApproveLoading] = useState<boolean>(false)

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  const isMobile = useBreakpointValue({ base: true, md: false })

  // 말미 0 제거 + 천단위 콤마
  const formatAmt = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const [int, frac = ''] = String(v).split('.')
    const fracTrimmed = frac.replace(/0+$/, '')
    const intComma = Number(int).toLocaleString()
    return fracTrimmed ? `${intComma}.${fracTrimmed}` : intComma
  }

  // 네트워크 문자열 정규화
  const normalizeNetwork = (raw?: string | null) => {
    const s = (raw || '').trim().toUpperCase()
    if (!s) return ''
    if (
      [
        'BSC',
        'BEP20',
        'BSC-20',
        'BINANCE',
        'BINANCE SMART CHAIN',
        'BNB'
      ].includes(s)
    )
      return 'BSC'
    if (['TRC', 'TRX', 'TRON', 'TRC20'].includes(s)) return 'TRC'
    return s
  }

  // 익스플로러 URL
  const getTxUrl = (network?: string | null, txid?: string | null) => {
    if (!txid) return ''
    switch (normalizeNetwork(network)) {
      case 'BSC':
        return `https://bscscan.com/tx/${txid}`
      case 'TRC':
        return `https://tronscan.org/#/transaction/${txid}`
      default:
        return ''
    }
  }

  // 공통 포맷터
  const formatDate = (v?: string | null) => {
    if (!v) return '-'
    const d = dayjs(v)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : v
  }

  const formatUSDT = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const [int, frac = ''] = String(v).split('.')
    const fracTrimmed = frac.replace(/0+$/, '')
    const intComma = Number(int).toLocaleString()
    return fracTrimmed ? `${intComma}.${fracTrimmed}` : intComma
  }

  // 클립보드 복사
  const copy = async (text?: string | null, label = '복사') => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: `${label} 완료`, status: 'success', duration: 1200 })
    } catch {
      toast({ title: `${label} 실패`, status: 'error', duration: 1500 })
    }
  }

  // 리스트 조회
  const fetchList = async (
    override?: Partial<{ page: number; pageSize: number; keyword: string }>
  ) => {
    try {
      setLoading(true)
      const params = { page, pageSize, keyword, ...override }
      const res = await getDepositList(params)
      const payload = res?.data?.data
      setItems(payload?.items ?? [])
      setTotal(payload?.total ?? 0)
      if (typeof payload?.page === 'number') setPage(payload.page)
      if (typeof payload?.pageSize === 'number') setPageSize(payload.pageSize)
    } catch (e: any) {
      toast({
        title: '입금 목록 조회 실패',
        description: e?.message ?? '네트워크 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const onSearch = () => {
    setPage(1)
    fetchList({ page: 1 })
  }

  // ⭐️ 승인 버튼 클릭 → 모달 오픈
  const openApproveModal = (d: DepositItem) => {
    setSelected(d)
    setApproveAmount('') // ⭐️ 무조건 빈 값으로 초기화
    onOpen()
  }

  // ⭐️ 승인 실행
  const handleApprove = async () => {
    if (!selected) return
    const amountNum = Number(approveAmount)
    if (!approveAmount || Number.isNaN(amountNum) || amountNum <= 0) {
      toast({
        title: '유효한 금액을 입력하세요.',
        status: 'warning',
        duration: 1800
      })
      return
    }
    if (!auth?.id) {
      toast({
        title: '관리자 정보가 없습니다.',
        status: 'error',
        duration: 1800
      })
      return
    }

    try {
      setApproveLoading(true)
      await approveTRC({
        adminId: auth.id,
        historyId: selected.id,
        amount: amountNum
      })
      toast({ title: '입금 승인 완료', status: 'success', duration: 1600 })
      onClose()
      setSelected(null)
      setApproveAmount('')
      // 최신 데이터 반영
      fetchList()
    } catch (e: any) {
      toast({
        title: '입금 승인 실패',
        description:
          e?.response?.data?.message || e?.message || '오류가 발생했습니다.',
        status: 'error',
        duration: 2600,
        isClosable: true
      })
    } finally {
      setApproveLoading(false)
    }
  }

  // 모바일 카드 UI
  const MobileCard = ({ d }: { d: DepositItem }) => {
    const isTRC = normalizeNetwork(d.network) === 'TRC'
    return (
      <Box borderWidth="1px" borderRadius="lg" p={4}>
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontWeight="bold">{d.username || '-'}</Text>
          <Badge colorScheme="green" variant="subtle">
            입금
          </Badge>
        </Flex>
        <Text fontSize="sm" color="gray.400">
          @{d.account || '-'}
        </Text>
        <Divider my={3} />

        <Stack spacing={1} fontSize="sm">
          <Flex justify="space-between" align="center">
            <Text color="gray.500">금액(USDT)</Text>
            <Text fontWeight="semibold">{formatUSDT(d.usdtAmount)}</Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text color="gray.500">네트워크</Text>
            <Badge colorScheme={isTRC ? 'orange' : 'blue'} variant="subtle">
              {d.network || '-'}
            </Badge>
          </Flex>

          <Flex justify="space-between" align="center" gap={2}>
            <Text color="gray.500">지갑주소</Text>
            <HStack spacing={1} maxW="65%">
              <Tooltip label={d.walletAddress || ''} hasArrow>
                <Text noOfLines={1}>{d.walletAddress || '-'}</Text>
              </Tooltip>
              {d.walletAddress && (
                <IconButton
                  aria-label="copy address"
                  size="xs"
                  variant="ghost"
                  icon={<CopyIcon />}
                  onClick={() => copy(d.walletAddress, '지갑주소')}
                />
              )}
            </HStack>
          </Flex>

          <Flex justify="space-between" align="center" gap={2}>
            <Text color="gray.500">TXID</Text>
            <HStack spacing={1} maxW="65%">
              <Tooltip label={d.txid || ''} hasArrow>
                <Text noOfLines={1}>{d.txid || '-'}</Text>
              </Tooltip>
              {d.txid && (
                <>
                  <IconButton
                    aria-label="open tx in explorer"
                    size="xs"
                    variant="ghost"
                    icon={
                      <Icon viewBox="0 0 24 24">
                        <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h5V3H3v7h2V5zM5 19h14V9h2v12H3V9h2v10z" />
                      </Icon>
                    }
                    isDisabled={!getTxUrl(d.network, d.txid)}
                    onClick={() => {
                      const url = getTxUrl(d.network, d.txid)
                      if (url) window.open(url, '_blank', 'noopener,noreferrer')
                      else
                        toast({
                          title: '지원하지 않는 네트워크',
                          status: 'warning',
                          duration: 1500
                        })
                    }}
                  />
                  <IconButton
                    aria-label="copy txid"
                    size="xs"
                    variant="ghost"
                    icon={<CopyIcon />}
                    onClick={() => copy(d.txid, 'TXID')}
                  />
                </>
              )}
            </HStack>
          </Flex>

          <Flex justify="space-between">
            <Text color="gray.500">연락처</Text>
            <Text>{d.phone || '-'}</Text>
          </Flex>

          <Flex justify="space-between">
            <Text color="gray.500">입금일시</Text>
            <Text>{formatDate(d.createdAt)}</Text>
          </Flex>
        </Stack>

        {/* ⭐️ TRC 전용 승인 버튼 */}
        {isTRC && d.usdtAmount === null && (
          <Button
            mt={3}
            size="sm"
            colorScheme="orange"
            onClick={() => openApproveModal(d)}
          >
            승인(트론)
          </Button>
        )}
      </Box>
    )
  }

  return (
    <DefaultLayout>
      <AdminNav />

      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* 검색/필터 */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          mb={3}
          gap={3}
        >
          <Heading as="h2" size="md" color="white">
            입금 관리
          </Heading>

          <HStack spacing={3} align="stretch">
            <InputGroup width={{ base: '100%', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} />
              </InputLeftElement>
              <Input
                placeholder="아이디/이름/연락처/TXID"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              />
            </InputGroup>
            <Button onClick={onSearch} colorScheme="orange" bgGradient="linear(to-r, brand.500, blue.500)" color="white" _hover={{ bgGradient: "linear(to-r, brand.400, blue.400)" }} minW={{ base: 'auto', md: '70px' }}>
              검색
            </Button>
            <Select
              width={{ base: '110px', md: '120px' }}
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                setPage(1)
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}개씩
                </option>
              ))}
            </Select>
          </HStack>
        </Flex>

        {/* 총 건수/페이지 */}
        <Flex justify="space-between" align="center" mb={2}>
          <Text color="gray.400" fontSize="sm">
            총 <b>{total.toLocaleString()}</b>건
          </Text>
          <Text color="gray.500" fontSize="sm">
            페이지 {page} / {totalPages}
          </Text>
        </Flex>

        {/* 리스트 */}
        {loading ? (
          <Flex align="center" justify="center" py={8} gap={2}>
            <Spinner />
            <Text>불러오는 중…</Text>
          </Flex>
        ) : items.length === 0 ? (
          <Flex align="center" justify="center" py={8}>
            <Text color="gray.500">데이터가 없습니다.</Text>
          </Flex>
        ) : isMobile ? (
          <Stack spacing={3}>
            {items.map((d) => (
              <MobileCard key={d.id} d={d} />
            ))}
          </Stack>
        ) : (
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table size="sm">
              <Thead bg="dark.card">
                <Tr>
                  <Th>번호</Th>
                  <Th>아이디</Th>
                  <Th>이름</Th>
                  <Th>연락처</Th>
                  <Th isNumeric>입금 USDT</Th>
                  <Th>잔액 (전 → 후)</Th>
                  <Th>네트워크</Th>
                  <Th>TXID</Th>
                  <Th>생성일</Th>
                  {/* ⭐️ 액션 컬럼 */}
                  <Th>액션</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((d) => {
                  const isTRC = normalizeNetwork(d.network) === 'TRC'
                  return (
                    <Tr key={d.id}>
                      <Td>{d.id}</Td>
                      <Td>{d.account || '-'}</Td>
                      <Td>{d.username || '-'}</Td>
                      <Td>{d.phone || '-'}</Td>
                      <Td isNumeric>{formatUSDT(d.usdtAmount)}</Td>
                      <Td>
                        {formatAmt(d.beforeUsdtBalance)} →{' '}
                        {formatAmt(d.afterUsdtBalance)}
                      </Td>
                      <Td>
                        <Badge
                          variant="subtle"
                          colorScheme={isTRC ? 'orange' : 'blue'}
                        >
                          {d.network || '-'}
                        </Badge>
                      </Td>

                      {/* ⭐️ TXID 셀을 Td로 감쌈 (기존 마크업 오류 수정) */}
                      <Td>
                        <HStack spacing={1} maxW="100%">
                          <Tooltip label={d.txid || ''} hasArrow>
                            <Text noOfLines={1}>{d.txid || '-'}</Text>
                          </Tooltip>

                          {d.txid && (
                            <>
                              <IconButton
                                aria-label="open tx in explorer"
                                size="xs"
                                variant="ghost"
                                icon={
                                  <Icon viewBox="0 0 24 24">
                                    <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h5V3H3v7h2V5zM5 19h14V9h2v12H3V9h2v10z" />
                                  </Icon>
                                }
                                isDisabled={!getTxUrl(d.network, d.txid)}
                                onClick={() => {
                                  const url = getTxUrl(d.network, d.txid)
                                  if (url)
                                    window.open(
                                      url,
                                      '_blank',
                                      'noopener,noreferrer'
                                    )
                                  else
                                    toast({
                                      title: '지원하지 않는 네트워크',
                                      status: 'warning',
                                      duration: 1500
                                    })
                                }}
                              />

                              <IconButton
                                aria-label="copy txid"
                                size="xs"
                                variant="ghost"
                                icon={<CopyIcon />}
                                onClick={() => copy(d.txid, 'TXID')}
                              />
                            </>
                          )}
                        </HStack>
                      </Td>

                      <Td>{formatDate(d.createdAt)}</Td>

                      {/* ⭐️ 액션 (TRC만 노출) */}
                      <Td>
                        {isTRC && d.usdtAmount === null ? (
                          <Button
                            size="xs"
                            colorScheme="orange"
                            onClick={() => openApproveModal(d)}
                          >
                            승인(트론)
                          </Button>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            -
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* 페이지네이션 */}
        <Flex align="center" justify="center" gap={2} mt={4}>
          <Button
            onClick={() => setPage(1)}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            처음
          </Button>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            이전
          </Button>
          <Text fontSize="sm">
            {page} / {totalPages}
          </Text>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            다음
          </Button>
          <Button
            onClick={() => setPage(totalPages)}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            마지막
          </Button>
        </Flex>
      </Box>

      {/* ⭐️ 승인 모달 */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!approveLoading) onClose()
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>TRC 입금 승인</ModalHeader>
          <ModalBody>
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.400">
                사용자: <b>{selected?.username || '-'}</b> (@
                {selected?.account || '-'})
              </Text>
              <Text fontSize="sm" color="gray.400">
                이력번호: <b>{selected?.id ?? '-'}</b>
              </Text>
              <Text fontSize="sm" color="gray.400">
                기본 입금액: <b>{formatUSDT(selected?.usdtAmount)}</b>
              </Text>

              <Box>
                <Text mb={1} fontWeight="semibold">
                  승인 금액(USDT)
                </Text>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={approveAmount}
                  onChange={(e) => setApproveAmount(e.target.value)}
                  placeholder="금액을 입력하세요"
                />
                {/* ⭐️ 퀵버튼 */}
                <HStack mt={2} spacing={2}>
                  {[200, 2400, 4000, 5000].map((amt) => (
                    <Button
                      key={amt}
                      size="xs"
                      variant="outline"
                      onClick={() => setApproveAmount(String(amt))}
                    >
                      {amt}
                    </Button>
                  ))}
                </HStack>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose} isDisabled={approveLoading}>
              취소
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleApprove}
              isLoading={approveLoading}
            >
              승인
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default Deposit
